import { useEffect, useRef } from 'react';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ThirdpartyRedirectorInterface } from '../../../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { getParameter } from '../../../../../utilities/urlParametersValidator';
import { applicationParameters } from '../../../../../utilities/application-parameters';
import { parentWindow } from '../../../../../utilities/embedded-iframe';

export const makeMessage = (url: string) => {
  return JSON.stringify({
    type: 'redirect',
    data: { url },
  });
};

const notifyParent = (message: string) => {
  const targetOrigin = getParameter(TinkLinkUrlParameter.REDIRECT_URI) || '*';

  parentWindow.postMessage(message, targetOrigin);
};

type PostRedirectMessagePayload = {
  url: string;
  notifyParentHandler?: typeof notifyParent;
};

export const postRedirectMessage = ({
  url,
  notifyParentHandler = notifyParent,
}: PostRedirectMessagePayload) => {
  const message = makeMessage(url);

  notifyParentHandler(message);
};

export const usePostRedirectMessage = (
  thirdPartyRedirector: ThirdpartyRedirectorInterface | undefined
) => {
  const redirectMessagePosted = useRef(false);

  // On iOS devices we post a message to parent containing 3rd party url which can be used
  // to programatically redirect user to 3rd party
  useEffect(() => {
    if (userAgent.isMobile && thirdPartyRedirector && !redirectMessagePosted.current) {
      redirectMessagePosted.current = true;
      const isTinkLinkEmbedded = window.top !== window;

      const shouldPostRedirectMessage = isTinkLinkEmbedded && applicationParameters.get('isIframe');

      if (shouldPostRedirectMessage) {
        postRedirectMessage({ url: thirdPartyRedirector.url });
      }
    }
  }, [thirdPartyRedirector]);
};

export type EmbeddedWindow = {
  top: Window['top'];
  self: Window['self'];
};

type UseRedirectPostMessageParams = {
  redirectUrl: string | null;
  currentWindow?: EmbeddedWindow;
  outerWindow?: typeof parentWindow;
};

export const useRedirectPostMessage = ({
  redirectUrl,
  currentWindow = window,
  outerWindow = parentWindow,
}: UseRedirectPostMessageParams) => {
  const redirectMessagePosted = useRef(false);

  // On iOS devices we post a message to parent containing 3rd party url which can be used
  // to programmatically redirect user to 3rd party
  useEffect(() => {
    if (userAgent.isMobile && redirectUrl && !redirectMessagePosted.current) {
      redirectMessagePosted.current = true;
      const isTinkLinkEmbedded = currentWindow.top !== currentWindow.self;

      const shouldPostRedirectMessage = isTinkLinkEmbedded && applicationParameters.get('isIframe');

      if (shouldPostRedirectMessage) {
        const message = makeMessage(redirectUrl);
        const targetOrigin = getParameter(TinkLinkUrlParameter.REDIRECT_URI) || '*';

        outerWindow.postMessage(message, targetOrigin);
      }
    }
  }, [redirectUrl, currentWindow, outerWindow]);
};
